<template>
  <div class="min-h-screen flex flex-col items-center justify-center gap-6 px-4">
    <!-- Branding -->
    <div class="flex flex-col items-center gap-2">
      <img src="/eqsys-white.svg" alt="eqSys" class="h-12 w-auto" />
      <div class="text-sm" style="color:var(--color-muted)">{{ $t('login.subtitle') }}</div>
    </div>

    <div class="max-w-md w-full p-6 shadow-lg z-40 card">
      <h2 class="text-2xl font-semibold mb-2" style="color:var(--color-text)">
        {{ $t('passwordReset.submit') }}
      </h2>

      <!-- Invalid / missing token -->
      <div v-if="invalidLink" class="space-y-4">
        <div class="rounded p-4 text-sm text-red-400" style="background:var(--color-surface-2);border:1px solid var(--color-border)">
          {{ $t('ui.thisResetLinkIsInvalidOrHasExpiredRequestANewOne') }}
        </div>
        <div class="flex justify-end">
          <router-link to="/login/forgot" class="btn primary">{{ $t('ui.requestNewLink') }}</router-link>
        </div>
      </div>

      <!-- Success state -->
      <div v-else-if="success" class="space-y-4">
        <div class="rounded p-4 text-sm" style="background:var(--color-surface-2);border:1px solid var(--color-border);color:var(--color-text)">
          {{ $t('ui.yourPasswordHasBeenResetYouCanNowSignInWithYourNewPassword') }}
        </div>
        <div class="flex justify-end">
          <router-link to="/login" class="btn primary">{{ $t('ui.goToSignIn') }}</router-link>
        </div>
      </div>

      <!-- Confirm form -->
      <form v-else @submit.prevent="onSubmit" class="space-y-4">
        <p class="text-sm" style="color:var(--color-muted)">
          {{ $t('ui.enterANewPasswordForYourAccount') }}
        </p>

        <BaseFormField :label="$t('profile.newPassword')">
          <BaseInput
            v-model="newPassword"
            type="password"
            autocomplete="new-password"
            autofocus
            :placeholder="$t('ui.atLeast8Characters')"
          />
        </BaseFormField>

        <BaseFormField
          :label="$t('profile.confirmPassword')"
          :error="confirmPassword && !passwordsMatch ? $t('profile.passwordMismatch') : ''"
        >
          <BaseInput
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
          />
        </BaseFormField>

        <div v-if="error" class="text-sm text-red-400">{{ error }}</div>

        <div class="flex items-center justify-between gap-2">
          <router-link to="/login" class="btn">{{ $t('common.cancel') }}</router-link>
          <button type="submit" class="btn primary" :disabled="loading || !canSubmit">
            <span v-if="loading">{{ $t('common.saving') }}</span>
            <span v-else>{{ $t('passwordReset.submit') }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { confirmPasswordReset } from '@/api/api.js'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'

const { t } = useI18n()
const route = useRoute()

const newPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const error = ref(null)
const success = ref(false)
const invalidLink = ref(false)

const uid = ref('')
const token = ref('')

const passwordsMatch = computed(() => newPassword.value === confirmPassword.value)
const canSubmit = computed(
  () => newPassword.value.length >= 8 && passwordsMatch.value
)

onMounted(() => {
  uid.value = route.query?.uid ?? ''
  token.value = route.query?.token ?? ''
  if (!uid.value || !token.value) {
    invalidLink.value = true
  }
})

async function onSubmit() {
  if (!canSubmit.value) return
  loading.value = true
  error.value = null
  try {
    await confirmPasswordReset(uid.value, token.value, newPassword.value)
    success.value = true
  } catch (e) {
    const status = e.status
    const data = e.data ?? {}
    if (status === 400) {
      // Could be invalid/expired token or weak password
      const detail = data.detail || data.error
      if (detail) {
        error.value = detail
      } else {
        // Try to surface field-level errors (e.g. new_password too common)
        const fieldMsg = Object.values(data).flat().find((v) => typeof v === 'string')
        error.value = fieldMsg || t('ui.invalidOrExpiredLinkPleaseRequestANewOne')
      }
    } else {
        error.value = t('ui.somethingWentWrongTryRequestingANewResetLink')
    }
  } finally {
    loading.value = false
  }
}
</script>
